import {Popover as PopoverPrimitive} from '@base-ui/react/popover'
import {useTranslation} from '@/translations/i18n.tsx'
import {MessageSmileSquareIcon, XCloseIcon} from '@components/ui/icon'
import {AiChatBody} from '@features/ai-chatbot/components/ai-chat-body'
import {CHAT_LAUNCHER_CLASSNAME} from '@features/ai-chatbot/constants.ts'
import {useChatStore} from '@features/ai-chatbot/store/store.ts'
import {cn} from '@utilities/helpers'

/**
 * Desktop AI assistant: a launcher anchoring a popover panel. Hidden on mobile,
 * where `AiChatDialog` takes over with a fullscreen dialog.
 */
export const AiChatPopover = () => {
    const {t} = useTranslation()
    const isOpen = useChatStore(state => state.isOpen)
    const setIsOpen = useChatStore(state => state.setIsOpen)

    return (
        <PopoverPrimitive.Root
            onOpenChange={(open, eventDetails) => {
                // The panel survives clicks elsewhere on the page: losing a
                // half-written message to a stray click is worse than having to
                // close it explicitly.
                if (eventDetails.reason !== 'outside-press') {
                    setIsOpen(open)
                }
            }}
            open={isOpen}
        >
            <PopoverPrimitive.Trigger
                aria-label={t('ai_chatbot:header')}
                className={cn(CHAT_LAUNCHER_CLASSNAME, 'max-md:hidden')}
                type="button"
            >
                {isOpen ? <XCloseIcon className="size-5" /> : <MessageSmileSquareIcon className="size-5" />}
            </PopoverPrimitive.Trigger>

            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Positioner
                    align="end"
                    className="z-modal outline-none max-md:hidden"
                    side="top"
                    sideOffset={8}
                >
                    <PopoverPrimitive.Popup
                        className="flex h-[min(720px,calc(100dvh-6rem))] w-100 flex-col overflow-hidden rounded-xl bg-white shadow-xl"
                        data-slot="ai-chat-popup"
                    >
                        <AiChatBody onClose={() => setIsOpen(false)} />
                    </PopoverPrimitive.Popup>
                </PopoverPrimitive.Positioner>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    )
}

AiChatPopover.displayName = 'AiChatPopover'
